import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';

import type { Reservation } from '@luxuryescapes/contract-svc-reservation';

import {
  ALLOWED_IMAGE_UPLOAD_ACCEPT,
  MAX_SVC_IMAGE_UPLOAD_BYTES,
  getUploadedImageId,
  uploadImage,
} from '~/services/ImageService';

import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

import DeleteImageDialog from './DeleteImageDialog';
import ImageEditor from './ImageEditor';
import type { LocalImageEdit } from './ImageEditor';
import ImagesByRoom from './ImagesByRoom';
import { getFullRoomNames } from './ImagesByRoom/utils';
import LogoVideoSection from './LogoVideoSection';
import MainImageLibrary from './MainImageLibrary';
import type { MediaImage, PropertyRoomTypeWithImages } from './types';
import {
  DEFAULT_ROOM_NAME,
  UNASSIGNED_IMAGE_LABEL,
  allSettledWithConcurrency,
  buildMediaImages,
  buildUploadPlans,
  dedupeMediaImages,
  getPrimaryVideoId,
  isAllowedImageFile,
  setFolderInputRef,
  togglePublicId,
} from './utils';
import type { UploadedRoomAssignment } from './utils';

const IMAGE_UPLOAD_CONCURRENCY = 5;

interface Props {
  images?: Array<App.OfferImage>;
  propertyId?: string;
  propertyLogoId?: string;
  propertyImages?: Array<Reservation.Image>;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  videos?: {
    luxuryescapes?: {
      vimeo_id?: string | null;
    };
  };
}

export default function ImagesVideo({
  images = [],
  propertyId,
  propertyLogoId,
  propertyImages,
  propertyRoomTypes,
  videos,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [uploadedImages, setUploadedImages] = useState<Array<MediaImage>>([]);
  const [uploadedRoomAssignments, setUploadedRoomAssignments] = useState<Array<UploadedRoomAssignment>>([]);
  const [deletedImagePublicIds, setDeletedImagePublicIds] = useState<Array<string>>([]);
  const [hiddenImagePublicIds, setHiddenImagePublicIds] = useState<Array<string>>([]);
  const [imagePendingDeletion, setImagePendingDeletion] = useState<MediaImage | undefined>();
  const [roomImagePendingEdit, setRoomImagePendingEdit] = useState<MediaImage | undefined>();
  const [roomImagePublicIdsByName, setRoomImagePublicIdsByName] = useState<Record<string, Array<string>>>({});
  const [imageEdits, setImageEdits] = useState<Record<string, LocalImageEdit>>({});
  const [imageSourceLabelOverrides, setImageSourceLabelOverrides] = useState<Record<string, string>>({});
  const [localVideoId, setLocalVideoId] = useState<string | undefined>();
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const videoId = localVideoId === '' ? undefined : (localVideoId ?? getPrimaryVideoId(videos));
  const mediaImages = useMemo(() => {
    const deletedImagePublicIdSet = new Set(deletedImagePublicIds);

    return dedupeMediaImages(
      buildMediaImages({
        offerImages: images,
        propertyImages,
        propertyRoomTypes,
      }).concat(uploadedImages),
    )
      .filter((image) => !deletedImagePublicIdSet.has(image.publicId))
      .map((image) => {
        const imageEdit = imageEdits[image.publicId];

        return {
          ...image,
          sourceLabel: imageSourceLabelOverrides[image.publicId] ?? imageEdit?.roomName ?? image.sourceLabel,
          title: imageEdit?.title ?? image.title,
        };
      });
  }, [
    deletedImagePublicIds,
    imageEdits,
    imageSourceLabelOverrides,
    images,
    propertyImages,
    propertyRoomTypes,
    uploadedImages,
  ]);
  const roomNames = useMemo(
    () => propertyRoomTypes?.map((room) => room.name).filter((roomName): roomName is string => !!roomName) ?? [],
    [propertyRoomTypes],
  );

  const uploadFiles = async (files: Array<File>, options: { ignoreNonImageFiles?: boolean } = {}) => {
    const candidateFiles = options.ignoreNonImageFiles ? files.filter(isAllowedImageFile) : files;
    const uploadableFiles = candidateFiles.filter(
      (file) => isAllowedImageFile(file) && file.size <= MAX_SVC_IMAGE_UPLOAD_BYTES,
    );
    const rejectedFileCount = candidateFiles.length - uploadableFiles.length;

    if (rejectedFileCount > 0) {
      enqueueSnackbar(
        `${rejectedFileCount} ${rejectedFileCount === 1 ? 'file was' : 'files were'} skipped. Use supported images under 5MB.`,
        { variant: 'warning' },
      );
    }

    if (uploadableFiles.length === 0) {
      return;
    }

    setIsUploadingImages(true);

    try {
      const uploadPlans = buildUploadPlans({
        deletedImagePublicIds,
        files: uploadableFiles,
        imageSourceLabelOverrides,
        propertyRoomTypes,
        uploadedRoomAssignments,
      });

      const uploadResults = await allSettledWithConcurrency(
        uploadPlans,
        IMAGE_UPLOAD_CONCURRENCY,
        async ({ file, roomAssignment }): Promise<{ image: MediaImage; roomAssignment?: UploadedRoomAssignment }> => {
          const response = await uploadImage(file);
          const uploadedImageId = getUploadedImageId(response);

          if (!uploadedImageId) {
            throw new Error('Image service did not return an image id');
          }

          return {
            image: {
              publicId: uploadedImageId,
              sourceLabel: roomAssignment?.roomName ?? UNASSIGNED_IMAGE_LABEL,
            },
            roomAssignment: roomAssignment
              ? {
                  ...roomAssignment,
                  publicId: uploadedImageId,
                }
              : undefined,
          };
        },
      );
      const uploadedMediaImages: Array<MediaImage> = [];
      const newUploadedRoomAssignments: Array<UploadedRoomAssignment> = [];

      uploadResults.forEach((result) => {
        if (result.status === 'fulfilled') {
          uploadedMediaImages.push(result.value.image);
          if (result.value.roomAssignment) {
            newUploadedRoomAssignments.push(result.value.roomAssignment);
          }
          return;
        }

        if (result.reason instanceof Error) {
          reportError(result.reason);
        }
      });

      const failedUploadCount = uploadResults.length - uploadedMediaImages.length;

      if (uploadedMediaImages.length > 0) {
        setUploadedImages((currentImages) => dedupeMediaImages(currentImages.concat(uploadedMediaImages)));
        setUploadedRoomAssignments((currentAssignments) => currentAssignments.concat(newUploadedRoomAssignments));
        enqueueSnackbar(
          `${uploadedMediaImages.length} ${uploadedMediaImages.length === 1 ? 'image' : 'images'} uploaded`,
          { variant: 'success' },
        );
      }

      if (failedUploadCount > 0) {
        enqueueSnackbar(`${failedUploadCount} ${failedUploadCount === 1 ? 'image' : 'images'} failed to upload`, {
          variant: 'error',
        });
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'Failed to upload images'), { variant: 'error' });
      if (error instanceof Error) {
        reportError(error);
      }
    } finally {
      setIsUploadingImages(false);
    }
  };

  const handleFolderInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);

    event.target.value = '';
    void uploadFiles(files, { ignoreNonImageFiles: true });
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);

    event.target.value = '';
    void uploadFiles(files);
  };

  const handleAssignImagesToRoom = (roomName: string, selectedImages: Array<MediaImage>) => {
    setImageSourceLabelOverrides((currentLabels) => {
      const nextLabels = { ...currentLabels };

      selectedImages.forEach((image) => {
        nextLabels[image.publicId] = roomName;
      });

      return nextLabels;
    });
    setImageEdits((currentEdits) => {
      const nextEdits = { ...currentEdits };

      selectedImages.forEach((image) => {
        if (nextEdits[image.publicId]) {
          nextEdits[image.publicId] = {
            ...nextEdits[image.publicId],
            roomName,
          };
        }
      });

      return nextEdits;
    });
  };

  const handleRemoveImageFromRoom = (publicId: string) => {
    setImageSourceLabelOverrides((currentLabels) => ({
      ...currentLabels,
      [publicId]: DEFAULT_ROOM_NAME,
    }));
    setImageEdits((currentEdits) =>
      currentEdits[publicId]
        ? {
            ...currentEdits,
            [publicId]: {
              ...currentEdits[publicId],
              roomName: DEFAULT_ROOM_NAME,
            },
          }
        : currentEdits,
    );
  };

  const handleDeleteImage = (publicId: string) => {
    setDeletedImagePublicIds((currentPublicIds) =>
      currentPublicIds.includes(publicId) ? currentPublicIds : currentPublicIds.concat(publicId),
    );
    setHiddenImagePublicIds((currentPublicIds) =>
      currentPublicIds.filter((currentPublicId) => currentPublicId !== publicId),
    );
    setImageSourceLabelOverrides((currentLabels) => {
      const nextLabels = { ...currentLabels };

      delete nextLabels[publicId];

      return nextLabels;
    });
    setImageEdits((currentEdits) => {
      const nextEdits = { ...currentEdits };

      delete nextEdits[publicId];

      return nextEdits;
    });
  };

  const handleToggleHiddenImage = (publicId: string) => {
    setHiddenImagePublicIds((currentPublicIds) => togglePublicId(currentPublicIds, publicId));
  };

  const handleEditRoomImage = (publicId: string) => {
    const image = mediaImages.find((mediaImage) => mediaImage.publicId === publicId);

    if (image) {
      setRoomImagePendingEdit(image);
    }
  };

  const handleDeleteRoomImageFromEditor = (image: MediaImage) => {
    setImagePendingDeletion(image);
  };

  const handleSaveImageEdit = (publicId: string, edit: LocalImageEdit) => {
    setImageEdits((currentEdits) => ({
      ...currentEdits,
      [publicId]: edit,
    }));
    setImageSourceLabelOverrides((currentLabels) => ({
      ...currentLabels,
      [publicId]: edit.roomName,
    }));
  };

  const handleCloseDeleteDialog = () => {
    setImagePendingDeletion(undefined);
  };

  const handleConfirmDelete = () => {
    if (!imagePendingDeletion) {
      return;
    }

    handleDeleteImage(imagePendingDeletion.publicId);
    setRoomImagePendingEdit((currentImage) =>
      currentImage?.publicId === imagePendingDeletion.publicId ? undefined : currentImage,
    );
    setImagePendingDeletion(undefined);
  };

  return (
    <Box>
      <input
        hidden
        accept={ALLOWED_IMAGE_UPLOAD_ACCEPT}
        multiple
        type="file"
        id="offer-builder-folder-upload-input"
        ref={setFolderInputRef}
        onChange={handleFolderInputChange}
      />
      <input
        hidden
        accept={ALLOWED_IMAGE_UPLOAD_ACCEPT}
        multiple
        type="file"
        id="offer-builder-image-upload-input"
        onChange={handleFileInputChange}
      />

      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <Typography variant="h4">Images & Video</Typography>
        <Typography variant="body1" color="text.secondary">
          Manage offer media from one place. Upload property images, organise them by room type, choose the property
          logo, and attach video content.
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1.5} sx={{ mb: 4 }}>
        <Button
          component="label"
          variant="outlined"
          htmlFor="offer-builder-folder-upload-input"
          disabled={isUploadingImages}
          startIcon={isUploadingImages ? <CircularProgress size={18} /> : <DriveFolderUploadOutlinedIcon />}
        >
          Upload folder
        </Button>
        <Button
          component="label"
          variant="contained"
          htmlFor="offer-builder-image-upload-input"
          disabled={isUploadingImages}
          startIcon={
            isUploadingImages ? <CircularProgress size={18} color="inherit" /> : <AddPhotoAlternateOutlinedIcon />
          }
        >
          Add image
        </Button>
      </Stack>

      <MainImageLibrary
        hiddenImagePublicIds={hiddenImagePublicIds}
        mediaImages={mediaImages}
        roomImagePublicIdsByName={roomImagePublicIdsByName}
        roomNames={roomNames}
        imageEdits={imageEdits}
        onDeleteImage={handleDeleteImage}
        onSaveImageEdit={handleSaveImageEdit}
        onToggleHiddenImage={handleToggleHiddenImage}
      />

      <ImagesByRoom
        excludedImagePublicIds={deletedImagePublicIds}
        hiddenImagePublicIds={hiddenImagePublicIds}
        mediaImages={mediaImages}
        onAssignImagesToRoom={handleAssignImagesToRoom}
        onEditImage={handleEditRoomImage}
        onRemoveImageFromRoom={handleRemoveImageFromRoom}
        onRoomImagesChange={setRoomImagePublicIdsByName}
        onToggleHiddenImage={handleToggleHiddenImage}
        propertyRoomTypes={propertyRoomTypes}
        uploadedRoomAssignments={uploadedRoomAssignments}
      />
      <LogoVideoSection
        propertyId={propertyId}
        propertyLogoId={propertyLogoId}
        videoId={videoId}
        onSaveVideo={setLocalVideoId}
      />
      <ImageEditor
        disabledRoomNames={
          roomImagePendingEdit ? getFullRoomNames(roomImagePublicIdsByName, roomImagePendingEdit.publicId) : []
        }
        edit={roomImagePendingEdit ? imageEdits[roomImagePendingEdit.publicId] : undefined}
        image={roomImagePendingEdit}
        open={!!roomImagePendingEdit}
        roomNames={roomNames}
        onClose={() => setRoomImagePendingEdit(undefined)}
        onDeleteImage={handleDeleteRoomImageFromEditor}
        onSave={handleSaveImageEdit}
      />
      <DeleteImageDialog
        open={!!imagePendingDeletion}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
}
